/**
 * WeatherIcon — petit icône météo SVG selon la description / température.
 *
 * Affiché dans l'en-tête Accueil pour un look premium (vs emoji ☀️).
 *
 * v1.12.216
 */

import React from 'react';
import Svg, { Circle, Path, Line, G } from 'react-native-svg';

function pickKind(desc, tempC, isDay) {
  const d = String(desc || '').toLowerCase();
  if (/neige|snow/.test(d)) return 'snow';
  if (/orage|thunder|storm/.test(d)) return 'storm';
  if (/pluie|rain|averse|drizzle/.test(d)) return 'rain';
  if (/brouillard|fog|brume|mist/.test(d)) return 'fog';
  if (/nuageux|cloud|couvert|overcast/.test(d)) return 'cloud';
  // Nuit (hors précipitations/nuages) → LUNE, jamais le soleil (même par temps chaud).
  if (isDay === false) return 'moon';
  if (typeof tempC === 'number' && tempC <= 5) return 'cold';
  if (typeof tempC === 'number' && tempC >= 24) return 'sun';
  return 'partly';
}

export default function WeatherIcon({ size = 16, color = '#1A1A1A', accent = '#D4AF72', desc, tempC, isDay = true }) {
  const kind = pickKind(desc, tempC, isDay);

  if (kind === 'moon') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M20 14.5A8 8 0 0 1 9.5 4a7 7 0 1 0 10.5 10.5z" fill={accent} stroke={color} strokeWidth="1.2" strokeLinejoin="round" />
      </Svg>
    );
  }

  if (kind === 'sun') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Circle cx="12" cy="12" r="4.5" fill={accent} />
        {[0,45,90,135,180,225,270,315].map(a => {
          const rad = (a * Math.PI) / 180;
          const x1 = 12 + Math.cos(rad) * 7;
          const y1 = 12 + Math.sin(rad) * 7;
          const x2 = 12 + Math.cos(rad) * 9.5;
          const y2 = 12 + Math.sin(rad) * 9.5;
          return <Line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.4" strokeLinecap="round" />;
        })}
      </Svg>
    );
  }

  if (kind === 'snow') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M7 14a3.5 3.5 0 1 1 1.2-6.8A4.5 4.5 0 0 1 17 9.5a3 3 0 0 1 .5 5.95H7.2A3.5 3.5 0 0 1 7 14z" fill="#E8EFF5" stroke={color} strokeWidth="1.3" />
        <G stroke={accent} strokeWidth="1.2" strokeLinecap="round">
          <Line x1="9"  y1="18" x2="9"  y2="21.5" />
          <Line x1="13" y1="18.5" x2="13" y2="22" />
          <Line x1="16.5" y1="18" x2="16.5" y2="21.5" />
        </G>
      </Svg>
    );
  }

  if (kind === 'rain') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M7 13a3.5 3.5 0 1 1 1.2-6.8A4.5 4.5 0 0 1 17 8.5a3 3 0 0 1 .5 5.95H7.2A3.5 3.5 0 0 1 7 13z" fill="#E8EFF5" stroke={color} strokeWidth="1.3" />
        <G stroke={accent} strokeWidth="1.4" strokeLinecap="round">
          <Line x1="9"  y1="17" x2="8"  y2="21" />
          <Line x1="13" y1="17" x2="12" y2="21" />
          <Line x1="17" y1="17" x2="16" y2="21" />
        </G>
      </Svg>
    );
  }

  if (kind === 'storm') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M7 12a3.5 3.5 0 1 1 1.2-6.8A4.5 4.5 0 0 1 17 7.5a3 3 0 0 1 .5 5.95H7.2A3.5 3.5 0 0 1 7 12z" fill="#E8EFF5" stroke={color} strokeWidth="1.3" />
        <Path d="M12.5 14L10 18.5h2.5L11 22l4-5h-2.5L14 14z" fill={accent} stroke={accent} strokeWidth="0.6" />
      </Svg>
    );
  }

  if (kind === 'fog') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M7 11a3.5 3.5 0 1 1 1.2-6.8A4.5 4.5 0 0 1 17 6.5a3 3 0 0 1 .5 5.95H7.2A3.5 3.5 0 0 1 7 11z" fill="#E8EFF5" stroke={color} strokeWidth="1.3" />
        <G stroke={color} strokeWidth="1.2" strokeLinecap="round">
          <Line x1="5"  y1="15" x2="17" y2="15" />
          <Line x1="7"  y1="18" x2="19" y2="18" />
          <Line x1="4"  y1="21" x2="14" y2="21" />
        </G>
      </Svg>
    );
  }

  if (kind === 'cold') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <G stroke={accent} strokeWidth="1.4" strokeLinecap="round">
          <Line x1="12" y1="3" x2="12" y2="21" />
          <Line x1="3" y1="12" x2="21" y2="12" />
          <Line x1="6" y1="6" x2="18" y2="18" />
          <Line x1="18" y1="6" x2="6" y2="18" />
        </G>
        <Circle cx="12" cy="12" r="2" fill={accent} />
      </Svg>
    );
  }

  if (kind === 'cloud') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path d="M7 16a3.5 3.5 0 1 1 1.2-6.8A4.5 4.5 0 0 1 17 11.5a3 3 0 0 1 .5 5.95H7.2A3.5 3.5 0 0 1 7 16z" fill="#FFFFFF" stroke={color} strokeWidth="1.4" />
      </Svg>
    );
  }

  // partly cloudy
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="9" cy="9" r="3.2" fill={accent} />
      <Path d="M10 17a3 3 0 1 1 1-5.85A3.8 3.8 0 0 1 18 13a2.5 2.5 0 0 1 .4 4.95H10.2A3 3 0 0 1 10 17z" fill="#FFFFFF" stroke={color} strokeWidth="1.4" />
    </Svg>
  );
}
